import React, { useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { formatCurrency } from '../../../../utils/currency';
import { CELL_SAVE_DEBOUNCE_MS } from '../../config';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';

interface AggregateMonthlyCellProps {
  aggregateRowId: number;
  year: number;
  month: number;
  target: number;
  actual: number;
  onTargetSaved: (year: number, month: number, newAmount: number) => void;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Per-month cell for an aggregate row: editable target on top, read-only
 * invoiced amount on bottom. Variance-coloured to match MonthlyCell.
 *
 * Diverges from MonthlyCell only in the save endpoint (upsert_aggregate_target
 * instead of upsert_project_forecast) and the variance reference (here it's
 * comparing actual vs target rather than actual vs forecast).
 */
const AggregateMonthlyCell: React.FC<AggregateMonthlyCellProps> = ({
  aggregateRowId,
  year,
  month,
  target,
  actual,
  onTargetSaved,
}) => {
  const supabase = useSupabaseClient();
  // Display the target with 2 decimals so it visually aligns with the
  // invoiced amount below (also 2 decimals) — keeps variance comparisons
  // visually consistent with what the cell colours are computed against.
  const [draft, setDraft] = useState<string>(target > 0 ? target.toFixed(2) : '');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedValue = useRef<number>(target);

  // Sync draft from the upstream target only on EXTERNAL changes. After our
  // own save the parent echoes back the value we just persisted — skipping
  // it here prevents in-flight keystrokes from being wiped.
  useEffect(() => {
    if (target === lastSavedValue.current) return;
    setDraft(target > 0 ? target.toFixed(2) : '');
    lastSavedValue.current = target;
  }, [target]);

  const persist = async (rawValue: string) => {
    const parsed = rawValue.trim() === '' ? 0 : parseFloat(rawValue);
    if (isNaN(parsed)) {
      setSaveState('error');
      return;
    }
    if (parsed === lastSavedValue.current) return;

    setSaveState('saving');
    const { error } = await supabase.rpc('upsert_aggregate_target', {
      p_aggregate_row_id: aggregateRowId,
      p_year: year,
      p_month: month,
      p_amount: parsed,
    });

    if (error) {
      console.error('Aggregate target save error:', error);
      notifyReadOnly(error);
      setSaveState('error');
      return;
    }

    lastSavedValue.current = parsed;
    setSaveState('saved');
    onTargetSaved(year, month, parsed);
    setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1200);
  };

  const handleChange = (value: string) => {
    setDraft(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => persist(value), CELL_SAVE_DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    // Normalise to 2-decimal display on blur so the column visually matches
    // the invoice line below.
    if (draft.trim()) {
      const parsed = parseFloat(draft);
      if (!isNaN(parsed)) {
        const normalised = parsed.toFixed(2);
        if (normalised !== draft) setDraft(normalised);
      }
    }
    persist(draft);
  };

  // Compare saved target vs actual invoiced. Same colour vocabulary as
  // MonthlyCell so the page reads consistently. Aggregate semantics:
  //   - invoiced over target → green (more billed than planned)
  //   - invoiced under target → red (under-billed)
  //   - invoiced with no target set → orange (target missing)
  const variance = (() => {
    if (actual <= 0) return 'neutral';
    const t = lastSavedValue.current;
    if (t <= 0) return 'orange';
    if (actual > t) return 'green';
    if (actual < t) return 'red';
    return 'neutral';
  })();

  const varianceTitle =
    variance === 'red'
      ? 'Invoiced less than target this month'
      : variance === 'green'
        ? 'Invoiced more than target this month'
        : variance === 'orange'
          ? 'Invoiced this month with no target set'
          : undefined;

  return (
    <div className="monthly-cell">
      <div className="monthly-cell__forecast-row">
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          className={`monthly-cell__input is-${variance}`}
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="—"
          title={varianceTitle}
        />
        {saveState === 'saving' && <span className="monthly-cell__indicator monthly-cell__indicator--saving">…</span>}
        {saveState === 'saved' && <span className="monthly-cell__indicator monthly-cell__indicator--saved">✓</span>}
        {saveState === 'error' && <span className="monthly-cell__indicator monthly-cell__indicator--error">!</span>}
      </div>
      <div className={`monthly-cell__actual ${actual > 0 ? 'is-active' : ''}`}>
        {actual > 0 ? formatCurrency(actual) : '—'}
      </div>
    </div>
  );
};

export default AggregateMonthlyCell;
